// @flow
import {hasTrackersWithoutConsent, loadTrackers, loadTrackersWithoutConsent,} from "../../actions/config";
import type {Tracking} from "../../models/Tracking";
import type {OrderReducerState} from "../../reducers/orderReducer";
import type {CouponReducerState} from "../../reducers/couponReducer";
import {
    cartSuccessChecked,
    checkoutPageStepSet,
    paymentSuccessVerified
} from "../../reducers/orderReducer";
import StatisticApi from "../../api/StatisticApi";
import {
    brandInstanceGotten,
    brandSelected,
    configSet,
    garageGroupIdResolved,
    trackerSuccessLoaded,
    trackerWithoutConsentSuccessLoaded
} from "../../reducers/configReducer";
import {categorySet, markerSet, searchSet, vehicleSet} from "../../reducers/filterReducer";
import {productSet} from "../../reducers/contentReducer";
import {fromCartRemoved, quantitySet, toCartAdded} from "../../reducers/cartReducer";
import {getMaxOrderableQuantity} from "../../models/Product";
import {bookmarkAdded, bookmarkRemoved} from "../../reducers/bookmarksReducer";
import {enquirySaved, shareButtonClicked, wishlistSharingSaved} from "../../reducers/mailingReducer";
import {CHECKOUT_PAGE_STATUS} from "../../components/checkout/CheckoutPage";
import {includesOfferMarker} from "../../models/Marker";

export const trackers: Tracking[] = [];

export const tracker = store => next => action => {
    try {
        // internal
        const configId = getConfigId(store);
        switch (action.type) {
            case toCartAdded.type: {
                const statisticApi = new StatisticApi(configId);
                statisticApi.trackProductAddedToCart(action.payload.product.id);
                break;
            }
            case bookmarkAdded.type: {
                const statisticApi = new StatisticApi(configId);
                statisticApi.trackAddedToWatchlist(action.payload.id);
                break;
            }
            case enquirySaved.type: {
                const statisticApi = new StatisticApi(configId);
                statisticApi.trackProductQuestion(action.payload.product.id);
                break;
            }
            case shareButtonClicked.type: {
                const statisticApi = new StatisticApi(configId);
                statisticApi.trackProductShared(action.payload.id);
                break;
            }
            default:
                break;
        }

        // third party tracker
        if (store.getState().consent.googleAnalytics || hasTrackersWithoutConsent()) {
            if (store.getState().config.trackerLoaded || store.getState().config.consentlessTrackerLoaded) {
                switch (action.type) {
                    case paymentSuccessVerified.type: {
                        const order: OrderReducerState = store.getState().order;
                        const coupon: CouponReducerState = store.getState().coupon;
                        trackers.forEach(tracker => tracker.logOrder(order, coupon));
                        trackers.forEach(tracker => tracker.clearCart());
                        break;
                    }
                    case toCartAdded.type: {
                        trackers.forEach(tracker => tracker.addItem(action.payload.product));
                        break;
                    }
                    case fromCartRemoved.type: {
                        // the reducer removes the whole cart line, so report its full amount
                        const item = store.getState().cart.items.find(item => item.product.id === action.payload.id);
                        trackers.forEach(tracker => tracker.removeItem(action.payload, item ? item.amount : 1));
                        break;
                    }
                    case quantitySet.type: {
                        // quantity spinner in the cart: report the difference as add/remove,
                        // clamped the same way the reducer clamps the new amount
                        const item = store.getState().cart.items.find(item => item.product.id === action.payload.id);
                        if (item) {
                            const target = Math.min(Math.max(action.payload.amount, 1), getMaxOrderableQuantity(item.product));
                            const delta = target - item.amount;
                            if (delta > 0) trackers.forEach(tracker => tracker.addItem(item.product, delta));
                            if (delta < 0) trackers.forEach(tracker => tracker.removeItem(item.product, -delta));
                        }
                        break;
                    }
                    case bookmarkAdded.type: {
                        trackers.forEach(tracker => tracker.insertToWatchlist(action.payload));
                        break;
                    }
                    case bookmarkRemoved.type: {
                        trackers.forEach(tracker => tracker.removeFromWatchlist(action.payload));
                        break;
                    }
                    case productSet.type: {
                        trackers.forEach(tracker => tracker.showProductDetails(action.payload.product));
                        break;
                    }
                    case searchSet.type: {
                        if (action.payload.searchPhrase) {
                            trackers.forEach(tracker => tracker.logSearch(action.payload.searchPhrase));
                        }
                        break;
                    }
                    case brandSelected.type: {
                        const brand = (action.payload || {}).brand;
                        if (brand && brand.manufacturerName) {
                            trackers.forEach(tracker => tracker.logSelectContent("brand", brand.manufacturerName));
                        }
                        break;
                    }
                    case categorySet.type: {
                        const category = action.payload.categories && action.payload.categories[0];
                        if (category && category.title) {
                            trackers.forEach(tracker => tracker.logSelectContent("category", category.title));
                        }
                        break;
                    }
                    case vehicleSet.type: {
                        const vehicle = action.payload.vehicle || action.payload.vehicleModel;
                        if (vehicle && vehicle.title) {
                            trackers.forEach(tracker => tracker.logSelectContent("carline", vehicle.title));
                        }
                        break;
                    }
                    case markerSet.type: {
                        if (Array.isArray(action.payload) && includesOfferMarker(action.payload)) {
                            trackers.forEach(tracker => tracker.logSelectContent("sale", "sale"));
                        }
                        break;
                    }
                    case enquirySaved.type: {
                        trackers.forEach(tracker => tracker.logGenerateLead([action.payload.product], "inquiry"));
                        break;
                    }
                    case wishlistSharingSaved.type: {
                        trackers.forEach(tracker => tracker.logGenerateLead(action.payload.products, "favourites"));
                        break;
                    }
                    case shareButtonClicked.type: {
                        trackers.forEach(tracker => tracker.logShare(action.payload));
                        break;
                    }
                    case cartSuccessChecked.type: {
                        trackers.forEach(tracker => tracker.logViewCart(store.getState().cart.items));
                        break;
                    }
                    case checkoutPageStepSet.type: {
                        // fire each checkout-funnel event once, when its step is reached the first time
                        const previousMax = store.getState().order.maxPage;
                        const step = action.payload;
                        const items = store.getState().cart.items;
                        const coupon: CouponReducerState = store.getState().coupon;
                        for (let reached = previousMax + 1; reached <= step; reached++) {
                            if (reached === CHECKOUT_PAGE_STATUS.ADRESS) {
                                trackers.forEach(tracker => tracker.logBeginCheckout(items, coupon));
                            }
                            if (reached === CHECKOUT_PAGE_STATUS.PAYMENT) {
                                trackers.forEach(tracker => tracker.logAddShippingInfo(items, coupon));
                            }
                            if (reached === CHECKOUT_PAGE_STATUS.OVERVIEW) {
                                const paymentType = (store.getState().order.payment || {}).paymentProvider;
                                trackers.forEach(tracker => tracker.logAddPaymentInfo(items, coupon, paymentType));
                            }
                        }
                        break;
                    }
                    default:
                        break;
                }
            } else {
                switch (action.type) {
                    case trackerWithoutConsentSuccessLoaded.type:
                    case trackerSuccessLoaded.type:
                    case brandInstanceGotten.type:
                    case garageGroupIdResolved.type: // arrives before the configuration the trackers are loaded from
                    case configSet.type:
                        break;
                    default:
                        if (store.getState().consent.googleAnalytics && !store.getState().config.trackerLoaded) {
                            store.dispatch(loadTrackers());
                        }
                        if (hasTrackersWithoutConsent() && !store.getState().config.consentlessTrackerLoaded) {
                            store.dispatch(loadTrackersWithoutConsent());
                        }
                        break;
                }
            }
        }
    } catch (e) {
        console.error(e);
    }

    return next(action);
};

const getConfigId = store => {
    try {
        return store.getState().config.dealerId || store.getState().config.dealerDetails.cfgKey || store.getState().config.dealerDetails.identifier.dealerNo;
    } catch (e) {
        return "";
    }
};
